<template>
  <div class="assistant" :class="[`assistant--${layout}`, { 'is-open': open }]">
    <BaseOrderModal
      v-if="openedQuote"
      :orderUUID="openedQuote"
      v-model="showQuoteModal"
      @closeOrder="openedQuote = ''"
    />
    <BaseProductModal
      v-if="viewProductCode"
      :code="viewProductCode"
      v-model="showProductModal"
      @input="!$event && (viewProductCode = null)"
    />

    <transition name="assistant-panel">
      <section
        v-if="open"
        class="assistant__panel"
        :style="panelStyle"
        @click="onPanelClick"
      >
        <header class="assistant__header">
          <span class="assistant__header-title">
            <i class="mdi mdi-robot"></i>
            {{ showList ? "Conversaciones" : conversationTitle || "Asistente" }}
          </span>
          <span class="assistant__header-actions">
            <span class="assistant__options">
              <button
                type="button"
                title="Opciones del panel"
                :class="{ 'is-active': showOptions }"
                @click="showOptions = !showOptions"
              >
                <i class="mdi mdi-tune"></i>
              </button>
              <div v-if="showOptions" class="assistant__options-menu">
                <!-- En el teléfono siempre es pantalla completa -->
                <template v-if="!isMobile">
                  <p class="assistant__options-title">Tamaño</p>
                  <button
                    v-for="option in layouts"
                    :key="option.value"
                    type="button"
                    :class="{ 'is-selected': option.value === layout }"
                    @click="chooseLayout(option.value)"
                  >
                    <i class="mdi" :class="option.icon"></i>
                    {{ option.label }}
                  </button>
                </template>
                <p class="assistant__options-title">Letra</p>
                <div class="assistant__text-size">
                  <button
                    type="button"
                    title="Letra más chica"
                    :disabled="textScale === textScales[0]"
                    @click="changeTextScale(-1)"
                  >
                    A−
                  </button>
                  <span>{{ Math.round(textScale * 100) }}%</span>
                  <button
                    type="button"
                    title="Letra más grande"
                    :disabled="textScale === textScales[textScales.length - 1]"
                    @click="changeTextScale(1)"
                  >
                    A+
                  </button>
                </div>
              </div>
            </span>
            <button
              type="button"
              :title="showList ? 'Volver al chat' : 'Conversaciones'"
              :class="{ 'is-active': showList }"
              @click="toggleList"
            >
              <i class="mdi mdi-forum-outline"></i>
            </button>
            <button
              type="button"
              title="Nueva conversación"
              :disabled="loading"
              @click="newConversation"
            >
              <i class="mdi mdi-plus"></i>
            </button>
            <button type="button" title="Cerrar" @click="open = false">
              <i class="mdi mdi-close"></i>
            </button>
          </span>
        </header>

        <div v-if="showList" class="assistant__messages">
          <ConversationList
            :conversations="conversations"
            :currentId="session.id"
            :loading="listLoading"
            :error="listError"
            :disabled="loading || conversationLoading"
            @new="newConversation"
            @open="openConversation"
            @remove="removeConversation"
          />
        </div>

        <!-- Los códigos enlazados en los textos de Claude (data-nkr-code)
             se abren aquí: v-html no lleva listeners -->
        <div
          v-else
          ref="scroller"
          class="assistant__messages"
          @click="onMessagesClick"
        >
          <p v-if="conversationLoading" class="assistant__hint">
            Abriendo conversación…
          </p>
          <div v-else-if="!entries.length" class="assistant__empty">
            <p>Pega o dicta una lista de códigos y te muestro las opciones.</p>
            <p>Ejemplos:</p>
            <ul>
              <li>"8-97358-879-0 x2, 1-87610-111, ME220745"</li>
              <li>"Alternador para un Isuzu NPR HD 2006 4HK1"</li>
              <li>"¿Cuántas hay de cada una en cada tienda?"</li>
              <li>"Es para Transportes Pérez, genérame la cotización"</li>
            </ul>
          </div>

          <template v-for="entry in entries">
            <div
              v-if="entry.kind === 'user'"
              :key="entry.id"
              class="assistant__bubble assistant__bubble--user"
            >
              {{ entry.text }}
            </div>
            <div
              v-else-if="entry.kind === 'assistant'"
              :key="entry.id"
              class="assistant__bubble assistant__bubble--assistant"
              v-html="formatText(entry.text, knownCodes)"
            ></div>
            <PartOptionsCard
              v-else-if="entry.kind === 'partOptions'"
              :key="entry.id"
              :cardId="entry.id"
              :groupKeys="entry.card.groups.map(group => group.key)"
              :items="items"
              @select="selectOption"
              @quantity="setQuantity"
              @toggle="toggleOption"
              @option-quantity="setOptionQuantity"
            />
            <CustomerOptionsCard
              v-else-if="entry.kind === 'customerOptions'"
              :key="entry.id"
              :cardId="entry.id"
              :customers="entry.card.customers"
              :selectedId="customer && customer.personaId"
              @select="selectCustomer"
            />
            <VehicleOptionsCard
              v-else-if="entry.kind === 'vehicleOptions'"
              :key="entry.id"
              :cardId="entry.id"
              :title="entry.card.title"
              :detail="entry.card.detail"
              :total="entry.card.total"
              :options="entry.card.options"
              :selectedKey="vehicle && vehicle.key"
              @select="selectVehicle"
            />
            <StockCard
              v-else-if="entry.kind === 'stock'"
              :key="entry.id"
              :rows="entry.card.rows"
            />
            <QuoteCard
              v-else-if="entry.kind === 'quote'"
              :key="entry.id"
              :quote="entry.card.quote"
              @open="openQuote"
            />
            <ShippingOptionsCard
              v-else-if="entry.kind === 'shippingOptions'"
              :key="entry.id"
              :cardId="entry.id"
              :options="entry.card.options"
              @select="selectShipping"
            />
          </template>

          <div v-if="loading" class="assistant__working">
            <span></span><span></span><span></span>
          </div>
          <p v-if="error" class="assistant__error">{{ error }}</p>
          <p v-if="saveError" class="assistant__error">{{ saveError }}</p>
        </div>

        <div
          v-if="!showList && (selectedItems.length || customer || vehicle)"
          class="assistant__summary"
        >
          <div>
            <strong>{{ selectedItems.length }} pieza(s)</strong>
            <span v-if="referenceTotal !== null">
              · ref. {{ money(referenceTotal) }}
            </span>
            <div class="assistant__summary-customer">
              {{ customer ? customer.name : "Sin cliente" }}
            </div>
            <div v-if="vehicle" class="assistant__summary-customer">
              <i class="mdi mdi-truck-outline"></i>
              {{ vehicle.label }} · {{ vehicle.engine }}
              <button
                type="button"
                class="assistant__summary-clear"
                title="Quitar vehículo"
                @click="vehicle = null"
              >
                <i class="mdi mdi-close"></i>
              </button>
            </div>
          </div>
          <!-- Ya cotizado tal cual: abrirla. Si cambió algo después, se
               actualiza esa misma: una conversación, una cotización -->
          <button
            v-if="quoteIsCurrent"
            type="button"
            class="assistant__summary-open"
            @click="openQuote(quote.orderNumber)"
          >
            <i class="mdi mdi-open-in-new"></i> Abrir {{ quote.reference }}
          </button>
          <div v-else class="assistant__summary-actions">
            <button
              type="button"
              :disabled="loading || !selectedItems.length"
              @click="
                send(
                  quote
                    ? `Actualiza la cotización ${quote.reference} con lo elegido ahora.`
                    : 'Genérame la cotización.'
                )
              "
            >
              {{
                quote
                  ? `Actualizar ${quote.reference}`
                  : "Generar cotización"
              }}
            </button>
            <a
              v-if="quote"
              class="assistant__summary-link"
              @click="openQuote(quote.orderNumber)"
            >
              Abrir {{ quote.reference }}
            </a>
          </div>
        </div>

        <!-- En el teléfono el dictado ocupa el pie sin teclado: el texto en
             grande y un botón de stop que se ve -->
        <div
          v-if="!showList && dictationState && isMobile"
          class="assistant__voice"
        >
          <p
            class="assistant__voice-text"
            :class="{ 'is-empty': !input.trim() }"
          >
            {{ input.trim() || "Te escucho…" }}
          </p>
          <button
            type="button"
            class="assistant__voice-stop"
            :class="{ 'is-listening': dictationState === 'listening' }"
            :disabled="dictationState === 'finishing'"
            @click="toggleDictation"
          >
            <i class="mdi" :class="dictationIcon"></i>
          </button>
          <span class="assistant__voice-hint">
            {{
              dictationState === "listening"
                ? "Toca para terminar"
                : "Terminando de transcribir…"
            }}
          </span>
        </div>

        <div
          v-else-if="!showList && dictationState"
          class="assistant__dictation"
        >
          <span class="assistant__working">
            <span></span><span></span><span></span>
          </span>
          {{
            dictationState === "listening"
              ? "Escuchando… dale stop cuando termines"
              : "Terminando de transcribir…"
          }}
        </div>

        <footer
          v-if="!showList && !(isMobile && dictationState)"
          class="assistant__composer"
        >
          <!-- Mientras se dicta el texto lo escribe el reconocimiento: no se
               edita ni se envía hasta que esté final -->
          <textarea
            ref="input"
            v-model="input"
            rows="2"
            placeholder="Escribe o dicta…"
            :readonly="!!dictationState"
            :class="{ 'is-transcribing': dictationState }"
            @keydown.enter.exact.prevent="send(input)"
          ></textarea>
          <button
            v-if="speechSupported"
            type="button"
            class="assistant__mic"
            :class="{ 'is-listening': dictationState === 'listening' }"
            :disabled="dictationState === 'finishing'"
            :title="dictationState ? 'Detener dictado' : 'Dictar'"
            @click="toggleDictation"
          >
            <i class="mdi" :class="dictationIcon"></i>
          </button>
          <button
            type="button"
            :title="dictationState ? 'Esperando la transcripción' : 'Enviar'"
            :disabled="loading || !!dictationState || !input.trim()"
            @click="send(input)"
          >
            <i class="mdi mdi-send"></i>
          </button>
        </footer>
      </section>
    </transition>

    <button
      type="button"
      class="assistant__fab"
      :title="open ? 'Cerrar asistente' : 'Asistente'"
      @click="toggle"
    >
      <i class="mdi" :class="open ? 'mdi-close' : 'mdi-robot'"></i>
    </button>
  </div>
</template>

<script>
import { runAssistantTurn } from "./runAssistant.js";
import { SYSTEM_PROMPT, SELECTION_NOTE_PREFIX } from "./systemPrompt.js";
import tools from "./tools/index.js";
import { isSpeechSupported, startDictation } from "./speech.js";
import { money, formatText } from "./format.js";
import {
  LAYOUTS,
  getLayout,
  setLayout,
  getDockedOpen,
  setDockedOpen,
  TEXT_SCALES,
  getTextScale,
  setTextScale
} from "./layout.js";
import PartOptionsCard from "./components/PartOptionsCard.vue";
import CustomerOptionsCard from "./components/CustomerOptionsCard.vue";
import StockCard from "./components/StockCard.vue";
import QuoteCard from "./components/QuoteCard.vue";
import ShippingOptionsCard from "./components/ShippingOptionsCard.vue";
import VehicleOptionsCard from "./components/VehicleOptionsCard.vue";
import ConversationList from "./components/ConversationList.vue";
import {
  listConversations,
  getConversation,
  saveConversation,
  deleteConversation,
  getLastConversationId,
  setLastConversationId
} from "./conversations.js";

// Hasta este ancho el panel ocupa toda la pantalla (igual que el CSS)
const MOBILE_QUERY = "(max-width: 640px)";

// Espera tras un cambio en las tarjetas antes de guardar: elegir varias
// opciones seguidas es un solo guardado
const SAVE_DEBOUNCE_MS = 800;
const TITLE_LENGTH = 80;

const STOP_MESSAGES = {
  refusal: "Claude no quiso responder a eso. Prueba a decirlo de otra forma.",
  max_tokens: "La respuesta se cortó. Prueba con una lista más corta.",
  max_rounds: "El asistente se trabó dando vueltas. Prueba de nuevo."
};

// Las piezas elegidas de un grupo de las tarjetas, con su cantidad: una en
// los de código (selectedId), las marcadas en los de texto (multiple:
// selected = { idOpción: cantidad })
const chosenOf = item => {
  if (item.multiple) {
    return Object.entries(item.selected || {})
      .map(([id, quantity]) => ({
        option: item.options.find(option => option.id === id),
        quantity
      }))
      .filter(entry => entry.option);
  }
  const option = item.options.find(o => o.id === item.selectedId);
  return option ? [{ option, quantity: item.quantity }] : [];
};

const textOf = content =>
  typeof content === "string"
    ? [content]
    : content.filter(block => block.type === "text").map(block => block.text);

const initialState = () => ({
  // La conversación abierta. Es un objeto propio de cada conversación para que
  // un guardado que termina después de cambiar a otra no le ponga su id.
  session: { id: null },
  // Historial de la Messages API, tal cual se manda (ver runAssistant.js)
  messages: [],
  // Tarjeta de cada tool_use, por su id
  cards: {},
  // Piezas pedidas, por código o búsqueda: { code, quantity, options,
  // selectedId } o, en las de texto, { multiple: true, selected } (chosenOf)
  items: {},
  // Vehículo elegido en find_vehicle: { key, ids, label, engine, family, image }
  vehicle: null,
  customer: null,
  // La última cotización creada o modificada aquí: { orderNumber, reference,
  // signature }. signature es la selección con la que quedó (ver
  // selectionSignature): si no cambió, el resumen ofrece abrirla.
  quote: null,
  // Última nota de selección enviada: sólo se manda otra si cambió
  lastNote: "",
  input: "",
  loading: false,
  error: null,
  saveError: null,
  conversationLoading: false
});

// Burbuja flotante del asistente. Todo lo que hace sale de las tools
// (tools/index.js); lo que eligen las tarjetas vive aquí y las tools lo leen
// con ctx.getSelection(). Cada conversación se guarda en el API
// (conversations.js) al terminar un turno y al cambiar algo en las tarjetas.
export default {
  name: "AssistantBubble",
  components: {
    PartOptionsCard,
    CustomerOptionsCard,
    StockCard,
    QuoteCard,
    ShippingOptionsCard,
    VehicleOptionsCard,
    ConversationList
  },
  // NkrCodeLink de las tarjetas abre el producto con esto
  provide() {
    return { openProduct: this.openProduct };
  },
  data() {
    return {
      ...initialState(),
      open: false,
      openedQuote: "",
      showQuoteModal: false,
      viewProductCode: null,
      showProductModal: false,
      speechSupported: isSpeechSupported(),
      // null, "listening" (hablando) o "finishing" (ya paró y falta el texto
      // final). En los dos no se puede enviar.
      dictationState: null,
      dictation: null,
      showList: false,
      conversations: [],
      listLoaded: false,
      listLoading: false,
      listError: null,
      // Los guardados van en fila: el que crea la conversación tiene que
      // terminar antes del siguiente para que éste ya lleve el id
      saveQueue: Promise.resolve(),
      saveTimer: null,
      // Mientras se carga una conversación los watchers no guardan
      restoring: false,
      isMobile: false,
      layouts: LAYOUTS,
      layout: getLayout(),
      textScales: TEXT_SCALES,
      textScale: getTextScale(),
      showOptions: false,
      // Lo visible de la pantalla con el teclado abierto: { height, top }
      viewport: null,
      // Estilos del body antes de bloquear el scroll de la página, y dónde
      // estaba
      pageLock: null
    };
  },
  computed: {
    activeDepartment() {
      return this.$store.getters["general/getActiveDepartment"];
    },
    // El primer mensaje del vendedor
    conversationTitle() {
      for (const message of this.messages) {
        if (message.role !== "user") continue;
        const text = textOf(message.content).find(
          t => !t.startsWith(SELECTION_NOTE_PREFIX)
        );
        if (text) return text.trim().slice(0, TITLE_LENGTH);
      }
      return "";
    },
    // Lo que se pinta: textos del usuario y de Claude, y en lugar de cada
    // tool_use su tarjeta. Los resultados de las tools y la nota de selección
    // son para Claude.
    entries() {
      const entries = [];
      this.messages.forEach((message, index) => {
        if (message.role === "user") {
          textOf(message.content)
            .filter(text => !text.startsWith(SELECTION_NOTE_PREFIX))
            .forEach((text, i) =>
              entries.push({ id: `u${index}-${i}`, kind: "user", text })
            );
          return;
        }
        message.content.forEach((block, i) => {
          if (block.type === "text" && block.text.trim()) {
            entries.push({
              id: `a${index}-${i}`,
              kind: "assistant",
              text: block.text
            });
          } else if (block.type === "tool_use" && this.cards[block.id]) {
            const card = this.cards[block.id];
            entries.push({ id: block.id, kind: card.type, card });
          }
        });
      });
      return entries;
    },
    // Una entrada por pieza elegida, con su cantidad: lo que va a la
    // cotización (en un grupo de varias, cada marcada es una)
    selectedItems() {
      return Object.values(this.items).flatMap(item =>
        chosenOf(item).map(({ option, quantity }) => ({
          ...item,
          option,
          quantity
        }))
      );
    },
    dictationIcon() {
      if (this.dictationState === "finishing") return "mdi-loading mdi-spin";
      return this.dictationState === "listening"
        ? "mdi-stop"
        : "mdi-microphone";
    },
    referenceTotal() {
      const priced = this.selectedItems.filter(
        item => item.option.price !== null
      );
      if (!priced.length) return null;
      return priced.reduce(
        (sum, item) => sum + item.option.price * item.quantity,
        0
      );
    },
    // Piezas, cantidades y cliente elegidos, para saber si la cotización ya
    // tiene exactamente esto
    // Los códigos NKR que salieron en las tarjetas: son los que se enlazan en
    // los textos de Claude (cualquier otro número podría ser un precio)
    knownCodes() {
      const codes = new Set();
      Object.values(this.cards).forEach(card => {
        (card.groups || []).forEach(group =>
          group.options.forEach(option => codes.add(option.nkrCode))
        );
        (card.rows || []).forEach(row => row.found && codes.add(row.nkrCode));
        if (card.quote) {
          card.quote.added.forEach(line => codes.add(line.nkrCode));
          card.quote.skipped.forEach(line => codes.add(line.nkrCode));
        }
      });
      return Array.from(codes).filter(Boolean);
    },
    selectionSignature() {
      return JSON.stringify([
        this.selectedItems
          .map(item => `${item.option.nkrCode}×${item.quantity}`)
          .sort(),
        this.customer ? this.customer.personaId : null
      ]);
    },
    quoteIsCurrent() {
      return !!this.quote && this.quote.signature === this.selectionSignature;
    },
    currentLayout() {
      return LAYOUTS.find(option => option.value === this.layout);
    },
    // Abierto a todo el alto en pantalla grande: LayoutLogged y BaseModal le
    // dejan sitio para que el panel no tape nada ni quede tapado
    docked() {
      return this.open && this.layout === "tall" && !this.isMobile;
    },
    // En el teléfono el panel se ajusta a lo que deja el teclado: 100dvh no
    // lo descuenta y el campo de texto quedaría tapado
    // El tamaño de la letra va como variable CSS (ver .assistant__messages)
    panelStyle() {
      const style = { "--assistant-text-scale": this.textScale };
      if (!this.isMobile || !this.viewport) return style;
      return {
        ...style,
        top: `${this.viewport.top}px`,
        height: `${this.viewport.height}px`
      };
    }
  },
  watch: {
    // Otra tienda es otro catálogo, otros precios y otros clientes: se guarda
    // lo pendiente y se abren las conversaciones de esa tienda
    async "activeDepartment.code"() {
      await this.flushSave();
      this.reset();
      this.conversations = [];
      this.listLoaded = false;
      if (this.open) this.loadConversations();
    },
    items: {
      deep: true,
      handler() {
        this.scheduleSave();
      }
    },
    customer() {
      this.scheduleSave();
    },
    vehicle() {
      this.scheduleSave();
    },
    open(open) {
      this.showOptions = false;
      this.syncPageLock();
      if (this.layout === "tall") setDockedOpen(open);
    },
    docked(docked) {
      this.$store.dispatch("general/updateAssistantDocked", docked);
    },
    isMobile() {
      this.syncPageLock();
    },
    layout() {
      this.syncPageLock();
    }
  },
  mounted() {
    this.mobileQuery = window.matchMedia(MOBILE_QUERY);
    this.onMobileChange = () => {
      this.isMobile = this.mobileQuery.matches;
    };
    this.onMobileChange();
    this.mobileQuery.addEventListener("change", this.onMobileChange);
    this.onViewport = () => {
      const vv = window.visualViewport;
      this.viewport = vv ? { height: vv.height, top: vv.offsetTop } : null;
      this.revealFocusedField();
    };
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", this.onViewport);
      window.visualViewport.addEventListener("scroll", this.onViewport);
    }
    // Sin enfocar el campo: la página que se está abriendo puede querer el foco
    if (this.layout === "tall" && !this.isMobile && getDockedOpen()) {
      this.open = true;
      this.loadConversations();
    }
  },
  beforeDestroy() {
    this.cancelDictation();
    this.flushSave();
    this.mobileQuery.removeEventListener("change", this.onMobileChange);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.onViewport);
      window.visualViewport.removeEventListener("scroll", this.onViewport);
    }
    this.unlockPage();
    if (this.docked)
      this.$store.dispatch("general/updateAssistantDocked", false);
  },
  methods: {
    money,
    formatText,
    toggle() {
      this.open = !this.open;
      if (!this.open) return;
      if (!this.listLoaded) this.loadConversations();
      this.focusInput();
    },
    reset() {
      this.cancelDictation();
      Object.assign(this.$data, initialState());
    },
    // Corta el dictado sin esperar el texto que falte
    cancelDictation() {
      if (this.dictation) this.dictation.abort();
      this.dictation = null;
      this.dictationState = null;
    },
    toggleList() {
      this.showList = !this.showList;
      if (this.showList) this.loadConversations();
      else this.focusInput();
    },
    // La lista de la tienda; la primera vez abre la última conversación usada
    async loadConversations() {
      const firstTime = !this.listLoaded;
      this.listLoaded = true;
      this.listLoading = true;
      this.listError = null;
      try {
        this.conversations = await listConversations();
      } catch (e) {
        this.listError = "No se pudieron cargar las conversaciones.";
        return;
      } finally {
        this.listLoading = false;
      }
      const lastId = getLastConversationId(this.activeDepartment.code);
      if (
        firstTime &&
        lastId &&
        !this.messages.length &&
        this.conversations.some(c => Number(c.id) === lastId)
      ) {
        this.openConversation(lastId);
      }
    },
    async openConversation(id) {
      if (this.loading) return;
      this.showList = false;
      if (id === this.session.id) return;
      await this.flushSave();
      this.reset();
      this.conversationLoading = true;
      try {
        const conversation = await getConversation(id);
        const uiState = conversation.uiState || {};
        this.restoring = true;
        Object.assign(this.$data, {
          session: { id: conversation.id },
          messages: conversation.messages.map(message =>
            Object.freeze(message)
          ),
          cards: uiState.cards || {},
          items: uiState.items || {},
          customer: uiState.customer || null,
          vehicle: uiState.vehicle || null,
          quote: uiState.quote || null,
          lastNote: uiState.lastNote || ""
        });
        setLastConversationId(this.activeDepartment.code, conversation.id);
        // Los watchers de items y customer corren antes que este nextTick
        this.$nextTick(() => {
          this.restoring = false;
        });
        this.scrollToEnd();
      } catch (e) {
        this.error = "No se pudo abrir la conversación.";
      } finally {
        this.conversationLoading = false;
      }
      this.focusInput();
    },
    async newConversation() {
      if (this.loading) return;
      await this.flushSave();
      this.reset();
      this.showList = false;
      setLastConversationId(this.activeDepartment.code, null);
      this.focusInput();
    },
    async removeConversation(id) {
      try {
        await deleteConversation(id);
      } catch (e) {
        this.listError = "No se pudo borrar la conversación.";
        return;
      }
      this.conversations = this.conversations.filter(
        conversation => Number(conversation.id) !== id
      );
      if (id === this.session.id) {
        clearTimeout(this.saveTimer);
        this.reset();
        this.showList = true;
      }
    },
    scheduleSave() {
      if (this.restoring || !this.messages.length) return;
      clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(() => this.persist(), SAVE_DEBOUNCE_MS);
    },
    // Guarda ya lo que esté esperando el debounce
    flushSave() {
      if (this.saveTimer) {
        clearTimeout(this.saveTimer);
        this.saveTimer = null;
        this.persist();
      }
      return this.saveQueue;
    },
    // Se toma la foto ahora y se manda cuando terminen los guardados
    // anteriores: si mientras tanto se cambia de conversación, éste igual
    // guarda la suya
    persist() {
      this.saveTimer = null;
      // A mitad de un turno el historial puede terminar en un tool_use sin
      // su resultado, que Claude rechaza al retomarlo; al terminar se guarda
      if (this.loading || !this.messages.length) return this.saveQueue;
      const session = this.session;
      const departmentCode = this.activeDepartment.code;
      const snapshot = {
        title: this.conversationTitle,
        messages: this.messages.slice(),
        uiState: JSON.parse(
          JSON.stringify({
            cards: this.cards,
            items: this.items,
            customer: this.customer,
            vehicle: this.vehicle,
            quote: this.quote,
            lastNote: this.lastNote
          })
        )
      };
      this.saveQueue = this.saveQueue.then(async () => {
        try {
          const id = await saveConversation({ id: session.id, ...snapshot });
          if (!session.id) {
            session.id = id;
            setLastConversationId(departmentCode, id);
          }
          if (this.session === session) this.saveError = null;
        } catch (e) {
          if (this.session === session) {
            this.saveError = "No se pudo guardar la conversación.";
          }
        }
      });
      return this.saveQueue;
    },
    // En el teléfono enfocar abre el teclado y tapa media pantalla: ahí sólo
    // se abre cuando el vendedor toca el campo
    focusInput() {
      if (this.isMobile) return;
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
    },
    chooseLayout(value) {
      this.layout = value;
      this.showOptions = false;
      setLayout(value);
      if (value === "tall") setDockedOpen(this.open);
    },
    // El menú queda abierto: se puede ir probando hasta dar con el tamaño
    changeTextScale(step) {
      const index = TEXT_SCALES.indexOf(this.textScale) + step;
      if (index < 0 || index >= TEXT_SCALES.length) return;
      this.textScale = TEXT_SCALES[index];
      setTextScale(this.textScale);
    },
    // El teclado achica el panel y lo de abajo de la conversación queda
    // fuera: el campo que se está editando (la cantidad de una tarjeta) se
    // trae al centro para ver lo que se escribe
    revealFocusedField() {
      if (!this.isMobile) return;
      this.$nextTick(() => {
        const field = document.activeElement;
        const scroller = this.$refs.scroller;
        if (field && scroller && scroller.contains(field)) {
          field.scrollIntoView({ block: "center", behavior: "smooth" });
        }
      });
    },
    // Tocar fuera del menú de opciones lo cierra
    onPanelClick(event) {
      if (this.showOptions && !event.target.closest(".assistant__options")) {
        this.showOptions = false;
      }
    },
    // Con el panel a pantalla completa la página de atrás no se mueve: si no,
    // el dedo arrastra la página en vez de la conversación
    syncPageLock() {
      const fullscreen = this.isMobile || this.layout === "fullscreen";
      if (this.open && fullscreen) this.lockPage();
      else this.unlockPage();
    },
    lockPage() {
      if (this.pageLock) return;
      const style = document.body.style;
      this.pageLock = {
        scrollY: window.scrollY,
        styles: {
          position: style.position,
          top: style.top,
          width: style.width,
          overflow: style.overflow
        }
      };
      Object.assign(style, {
        position: "fixed",
        top: `-${this.pageLock.scrollY}px`,
        width: "100%",
        overflow: "hidden"
      });
      this.onViewport();
    },
    unlockPage() {
      if (!this.pageLock) return;
      Object.assign(document.body.style, this.pageLock.styles);
      window.scrollTo(0, this.pageLock.scrollY);
      this.pageLock = null;
    },
    scrollToEnd() {
      this.$nextTick(() => {
        const scroller = this.$refs.scroller;
        if (scroller) scroller.scrollTop = scroller.scrollHeight;
      });
    },
    ctx() {
      const department = this.activeDepartment || {};
      return {
        collection: this.$store.getters["general/getActiveStore"].code,
        departmentCode: department.code,
        departamentoId: department.full ? department.full.departamentoId : null,
        departments: this.$store.getters["general/getDepartments"] || [],
        // El agente sólo modifica cotizaciones de quien lo está usando
        currentUserId: (this.$store.state.auth.currentUser || {}).id || null,
        getSelection: () => ({
          items: this.selectedItems,
          // Los códigos de todas las opciones de las tarjetas, elegidos o no:
          // update_quote (syncSelected) sólo quita de la cotización éstos
          offeredCodes: new Set(
            Object.values(this.items).flatMap(item =>
              item.options.map(option => String(option.nkrCode).toUpperCase())
            )
          ),
          customer: this.customer,
          vehicle: this.vehicle,
          quote: this.quote
        })
      };
    },
    onCard(toolUseId, card) {
      this.$set(this.cards, toolUseId, card);
      if (card.type === "partOptions") {
        card.groups.forEach(group => {
          const previous = this.items[group.key];
          const ids = group.options.map(option => option.id);
          // Con una sola opción no hay nada que elegir; si no, lo que ya
          // estaba elegido y sigue entre las opciones
          const only = group.options.length === 1 ? ids[0] : null;
          if (group.multiple) {
            const kept = Object.entries((previous && previous.selected) || {})
              .filter(([id]) => ids.includes(id))
              .reduce(
                (acc, [id, quantity]) => ({ ...acc, [id]: quantity }),
                {}
              );
            this.$set(this.items, group.key, {
              code: group.code,
              multiple: true,
              quantity: group.quantity,
              options: group.options,
              selected: only ? { [only]: group.quantity } : kept
            });
            return;
          }
          const keepsPrevious = previous && ids.includes(previous.selectedId);
          this.$set(this.items, group.key, {
            code: group.code,
            quantity: group.quantity,
            options: group.options,
            selectedId: only || (keepsPrevious ? previous.selectedId : null)
          });
        });
      } else if (
        card.type === "customerOptions" &&
        card.customers.length === 1
      ) {
        this.customer = card.customers[0];
      } else if (card.type === "vehicleOptions" && card.total === 1) {
        this.vehicle = card.options[0];
      } else if (card.type === "quote") {
        this.syncWithQuote(card.quote);
      }
      this.scrollToEnd();
    },
    // Las cantidades guardadas mandan (el API puede recortarlas al stock o
    // update_quote cambiarlas): las tarjetas pasan a mostrar las mismas, y la
    // cotización queda como la de esta conversación
    syncWithQuote(quote) {
      const quantityIn = option => {
        const line = quote.added.find(
          added =>
            String(added.nkrCode).toUpperCase() ===
            String(option.nkrCode).toUpperCase()
        );
        return line && line.quantity ? line.quantity : null;
      };
      Object.values(this.items).forEach(item => {
        chosenOf(item).forEach(({ option }) => {
          const quantity = quantityIn(option);
          if (!quantity) return;
          if (item.multiple) {
            item.selected = { ...item.selected, [option.id]: quantity };
          } else {
            item.quantity = quantity;
          }
        });
      });
      this.quote = {
        orderNumber: quote.orderNumber,
        reference: quote.reference,
        signature: this.selectionSignature,
        // Lo que le falta (quoteLines.js): va en cada nota de selección
        missing: quote.missing || []
      };
    },
    // Vehículo y cliente se eligen para seguir con algo (buscar la pieza para
    // ese vehículo, cotizar a ese cliente): elegirlos se lo dice a Claude, que
    // sigue solo. Las piezas no, que se eligen varias seguidas.
    selectVehicle(option) {
      this.vehicle = option;
      this.announceChoice(
        `Vehículo elegido: ${option.label} · ${option.engine}`
      );
    },
    selectCustomer(customer) {
      this.customer = customer;
      this.announceChoice(`Cliente elegido: ${customer.name}`);
    },
    // El servicio elegido lo pone Claude con quote_shipping
    selectShipping(option) {
      this.announceChoice(`Envío elegido: ${option.service}`);
    },
    // Mientras Claude responde no se manda: la elección va en la nota de
    // selección del próximo mensaje
    announceChoice(text) {
      if (this.loading || this.dictationState) return;
      this.send(text);
    },
    selectOption(key, optionId) {
      this.items[key].selectedId = optionId;
    },
    setQuantity(key, value) {
      this.items[key].quantity = Math.max(1, parseInt(value) || 1);
    },
    // Grupo de varias (búsqueda por texto): marcar o desmarcar una pieza, que
    // entra con la cantidad pedida
    toggleOption(key, optionId) {
      const item = this.items[key];
      const selected = { ...item.selected };
      if (selected[optionId]) delete selected[optionId];
      else selected[optionId] = item.quantity || 1;
      item.selected = selected;
    },
    setOptionQuantity(key, optionId, value) {
      const item = this.items[key];
      item.selected = {
        ...item.selected,
        [optionId]: Math.max(1, parseInt(value) || 1)
      };
    },
    // Lo elegido en las tarjetas, para que Claude lo sepa en el siguiente
    // mensaje (elegir no le manda nada)
    selectionNote() {
      const lines = Object.values(this.items).map(item => {
        const chosen = chosenOf(item);
        return `- ${item.code}: ${
          chosen.length
            ? chosen
                .map(
                  ({ option, quantity }) =>
                    `${option.nkrCode} ×${quantity} (${option.title})`
                )
                .join("; ")
            : "sin elegir"
        }`;
      });
      if (this.customer) {
        lines.push(
          `Cliente: ${this.customer.name} (#${this.customer.personaId})`
        );
      }
      if (this.vehicle) {
        lines.push(`Vehículo: ${this.vehicle.label} ${this.vehicle.engine}`);
      }
      if (this.quote) {
        lines.push(
          `Cotización de esta conversación: ${this.quote.reference}${
            this.quoteIsCurrent ? "" : " (la selección cambió después)"
          }${
            (this.quote.missing || []).length
              ? `. Le falta: ${this.quote.missing.join(", ")}`
              : ". Está completa"
          }`
        );
      }
      return lines.length ? [SELECTION_NOTE_PREFIX, ...lines].join("\n") : "";
    },
    async send(text) {
      const message = text.trim();
      if (!message || this.loading || this.dictationState) return;

      const content = [];
      const note = this.selectionNote();
      if (note && note !== this.lastNote) {
        content.push({ type: "text", text: note });
        this.lastNote = note;
      }
      content.push({ type: "text", text: message });
      this.messages.push(Object.freeze({ role: "user", content }));

      this.input = "";
      this.error = null;
      this.loading = true;
      this.scrollToEnd();
      // Si cambia de tienda a mitad del turno, lo que llegue después ya no es
      // de la conversación que está abierta
      const session = this.session;
      const isCurrent = () => this.session === session;
      try {
        const stopReason = await runAssistantTurn({
          system: SYSTEM_PROMPT,
          tools,
          messages: this.messages,
          ctx: this.ctx(),
          onCard: (toolUseId, card) => {
            if (isCurrent()) this.onCard(toolUseId, card);
          }
        });
        if (isCurrent()) this.error = STOP_MESSAGES[stopReason] || null;
      } catch (e) {
        if (isCurrent()) {
          this.error =
            e?.response?.data?.error ||
            e?.response?.data?.message ||
            e?.message ||
            "No se pudo hablar con el asistente.";
        }
      } finally {
        if (isCurrent()) {
          this.loading = false;
          clearTimeout(this.saveTimer);
          this.persist();
          this.scrollToEnd();
          this.focusInput();
        }
      }
    },
    toggleDictation() {
      if (this.dictationState === "listening") {
        this.dictationState = "finishing";
        this.dictation.stop();
        return;
      }
      if (this.dictationState) return;
      const before = this.input.trim();
      this.dictationState = "listening";
      // Dictando no hace falta el teclado: se cierra y queda la conversación
      if (this.isMobile && this.$refs.input) this.$refs.input.blur();
      // Un dictado cancelado puede avisar tarde: sólo cuenta el actual
      const dictation = startDictation({
        lang: (this.$i18n && this.$i18n.locale) === "en" ? "en-US" : "es-419",
        onText: text => {
          if (this.dictation !== dictation) return;
          this.input = before ? `${before} ${text}` : text;
        },
        onEnd: () => {
          if (this.dictation !== dictation) return;
          this.dictationState = null;
          this.dictation = null;
          this.focusInput();
        },
        onError: code => {
          if (this.dictation !== dictation) return;
          if (code === "not-allowed") {
            this.error = "El navegador no dio permiso para usar el micrófono.";
          } else if (code === "network") {
            this.error =
              "Sin conexión para transcribir: revisa el texto antes de enviarlo.";
          }
        }
      });
      this.dictation = dictation;
    },
    openQuote(orderNumber) {
      this.openedQuote = String(orderNumber);
      this.showQuoteModal = true;
    },
    openProduct(code) {
      this.viewProductCode = code;
      this.showProductModal = true;
    },
    onMessagesClick(event) {
      const link = event.target.closest("[data-nkr-code]");
      if (link) this.openProduct(link.dataset.nkrCode);
    }
  }
};
</script>

<style lang="scss" scoped>
.assistant__fab {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 45;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #1f2937;
  color: white;
  font-size: 24px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
  &:hover {
    background: #111827;
  }
}

.assistant__panel {
  position: fixed;
  right: 24px;
  bottom: 88px;
  z-index: 45;
  width: 420px;
  height: min(680px, calc(100vh - 120px));
  display: flex;
  flex-direction: column;
  background: #f9fafb;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}
// Los tamaños que elige el vendedor (layout.js). Pegado a la derecha o a toda
// la pantalla, el botón flotante taparía el de enviar: se cierra con la X.
// Acoplado, la página le deja sitio (LayoutLogged, .assistant-docked, con
// este mismo ancho): es una columna, sin sombra ni animación de entrada
@media (min-width: 641px) {
  .assistant--tall .assistant__panel {
    top: 0;
    right: 0;
    bottom: 0;
    height: 100vh;
    border-radius: 0;
    box-shadow: none;
    border-left: 1px solid #e5e7eb;
    &.assistant-panel-enter,
    &.assistant-panel-leave-to {
      transform: none;
    }
  }
  .assistant--fullscreen .assistant__panel {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    border-radius: 0;
    box-shadow: none;
  }
  // A todo el ancho los mensajes quedan en una columna legible, al centro
  .assistant--fullscreen {
    .assistant__messages,
    .assistant__summary,
    .assistant__dictation,
    .assistant__composer {
      padding-left: max(12px, calc((100% - 880px) / 2));
      padding-right: max(12px, calc((100% - 880px) / 2));
    }
  }
  .assistant--tall.is-open .assistant__fab,
  .assistant--fullscreen.is-open .assistant__fab {
    display: none;
  }
}

// En el teléfono, pantalla completa. El alto lo corrige panelStyle cuando se
// abre el teclado; la X del encabezado cierra, así que el botón flotante sobra
@media (max-width: 640px) {
  .assistant__panel {
    top: 0;
    right: 0;
    bottom: auto;
    left: 0;
    width: 100%;
    height: 100dvh;
    border-radius: 0;
    box-shadow: none;
  }
  .assistant.is-open .assistant__fab {
    display: none;
  }
}

.assistant__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #1f2937;
  color: white;
  font-weight: 600;
  button {
    margin-left: 8px;
    font-size: 18px;
    color: white;
    &:disabled {
      opacity: 0.5;
    }
    &.is-active {
      color: #93c5fd;
    }
  }
}
.assistant__header-title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.assistant__header-actions {
  flex-shrink: 0;
}
.assistant__options {
  position: relative;
}
.assistant__options-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 1;
  min-width: 190px;
  padding: 4px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  .assistant__header & button {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    height: auto;
    margin: 0;
    padding: 6px 8px;
    border-radius: 6px;
    color: #374151;
    font-size: 13px;
    font-weight: 500;
    text-align: left;
    i {
      font-size: 18px;
    }
    &:hover {
      background: #f3f4f6;
    }
    &.is-selected {
      color: #2563eb;
      background: #eff6ff;
    }
    &:disabled {
      opacity: 0.35;
      background: none;
    }
  }
  .assistant__header & .assistant__text-size button {
    justify-content: center;
    width: 40px;
    font-size: 15px;
    font-weight: 700;
  }
}
.assistant__options-title {
  padding: 6px 8px 2px;
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.assistant__text-size {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  padding: 0 4px 4px;
  span {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
  }
}
@media (max-width: 640px) {
  .assistant__header {
    padding: max(10px, env(safe-area-inset-top)) 8px 10px 14px;
    button {
      margin-left: 4px;
      width: 40px;
      height: 40px;
      font-size: 22px;
    }
  }
}
.assistant__hint {
  font-size: 13px;
  color: #6b7280;
}

.assistant__messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  // Al llegar al final el scroll no pasa a la página de atrás
  overscroll-behavior: contain;
  // Cada mensaje con su alto: sin esto, cuando la conversación no cabe, la
  // columna encoge las tarjetas con overflow (la tabla de stock) en lugar de
  // hacer scroll
  > * {
    flex-shrink: 0;
    // El tamaño de letra elegido (layout.js). Las tarjetas tienen sus
    // tamaños en px: zoom los escala todos juntos. Va en cada mensaje y no
    // en este contenedor para no tocar su scroll
    zoom: var(--assistant-text-scale, 1);
  }
}
.assistant__empty {
  font-size: 13px;
  color: #6b7280;
  ul {
    margin-top: 4px;
    padding-left: 16px;
    list-style: disc;
  }
}
.assistant__bubble {
  max-width: 88%;
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}
.assistant__bubble--user {
  align-self: flex-end;
  background: #2563eb;
  color: white;
}
.assistant__bubble--assistant {
  align-self: flex-start;
  background: white;
  border: 1px solid #e5e7eb;
  white-space: normal;
  // Códigos enlazados por formatText (v-html: sin el scope del componente).
  // Igual que NkrCodeLink
  ::v-deep .nkr-code-link {
    color: #2563eb;
    cursor: pointer;
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 2px;
    &:hover {
      text-decoration-style: solid;
    }
  }
}
.assistant__working {
  display: flex;
  gap: 4px;
  padding: 8px 4px;
  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #9ca3af;
    animation: assistant-dot 1s infinite ease-in-out;
    &:nth-child(2) {
      animation-delay: 0.15s;
    }
    &:nth-child(3) {
      animation-delay: 0.3s;
    }
  }
}
@keyframes assistant-dot {
  0%,
  80%,
  100% {
    opacity: 0.3;
  }
  40% {
    opacity: 1;
  }
}
.assistant__error {
  font-size: 12px;
  color: #b91c1c;
}

.assistant__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid #e5e7eb;
  background: white;
  font-size: calc(13px * var(--assistant-text-scale, 1));
  button {
    padding: 6px 10px;
    border-radius: 6px;
    background: #16a34a;
    color: white;
    font-size: calc(12px * var(--assistant-text-scale, 1));
    font-weight: 600;
    white-space: nowrap;
    &:disabled {
      opacity: 0.5;
    }
  }
}
.assistant__summary-customer {
  font-size: calc(11px * var(--assistant-text-scale, 1));
  color: #6b7280;
}
.assistant__summary-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}
.assistant__summary .assistant__summary-open {
  background: #2563eb;
}
.assistant__summary-link {
  font-size: 11px;
  color: #2563eb;
  cursor: pointer;
}
.assistant__summary .assistant__summary-clear {
  padding: 0 2px;
  background: none;
  color: #6b7280;
  font-size: 11px;
}

.assistant__dictation {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-top: 1px solid #e5e7eb;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 12px;
  .assistant__working {
    padding: 0;
  }
}

.assistant__composer {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 8px;
  border-top: 1px solid #e5e7eb;
  background: white;
  textarea {
    flex: 1;
    resize: none;
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: calc(13px * var(--assistant-text-scale, 1));
    // Texto todavía provisional: lo sigue escribiendo el dictado
    &.is-transcribing {
      color: #6b7280;
      font-style: italic;
      background: #f9fafb;
    }
  }
  button {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    font-size: 18px;
    color: #374151;
    &:hover {
      background: #f3f4f6;
    }
    &:disabled {
      opacity: 0.4;
    }
    &.is-listening {
      background: #fee2e2;
      color: #b91c1c;
    }
  }
}
@media (max-width: 640px) {
  .assistant__composer {
    padding-bottom: max(8px, env(safe-area-inset-bottom));
    // Con menos de 16px iOS hace zoom al enfocar
    textarea {
      font-size: max(16px, calc(16px * var(--assistant-text-scale, 1)));
    }
    button {
      width: 44px;
      height: 44px;
      font-size: 22px;
    }
    // En el teléfono se dicta más de lo que se escribe
    .assistant__mic {
      border-radius: 50%;
      background: #dc2626;
      color: white;
      &:hover {
        background: #b91c1c;
      }
    }
  }
  .assistant__bubble {
    font-size: 14px;
  }
  // Los campos de las tarjetas (cantidades), a 16px de verdad: con menos iOS
  // hace zoom a la página al enfocarlos y el campo queda fuera de la vista.
  // Se descuenta el zoom del tamaño de letra (ver .assistant__messages)
  .assistant__messages
    ::v-deep
    input:not([type="checkbox"]):not([type="radio"]) {
    font-size: max(calc(16px / var(--assistant-text-scale, 1)), 12px);
  }
}

// Dictado en el teléfono (ver la plantilla)
.assistant__voice {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px 16px max(16px, env(safe-area-inset-bottom));
  border-top: 1px solid #e5e7eb;
  background: white;
}
.assistant__voice-text {
  align-self: stretch;
  max-height: 30vh;
  overflow-y: auto;
  font-size: calc(17px * var(--assistant-text-scale, 1));
  line-height: 1.4;
  color: #111827;
  &.is-empty {
    color: #9ca3af;
    font-style: italic;
    text-align: center;
  }
}
.assistant__voice-stop {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: #dc2626;
  color: white;
  font-size: 34px;
  box-shadow: 0 6px 18px rgba(220, 38, 38, 0.35);
  &.is-listening {
    animation: assistant-pulse 1.4s infinite ease-out;
  }
  &:disabled {
    background: #9ca3af;
    box-shadow: none;
  }
}
.assistant__voice-hint {
  font-size: 13px;
  color: #6b7280;
}
@keyframes assistant-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.45);
  }
  70% {
    box-shadow: 0 0 0 18px rgba(220, 38, 38, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0);
  }
}

.assistant-panel-enter-active,
.assistant-panel-leave-active {
  transition: opacity 0.15s, transform 0.15s;
}
.assistant-panel-enter,
.assistant-panel-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
